interface GoalsEditUpdateConfig {
    ajaxUrl: string;
    nonce: string;
    deleteNonce?: string;
}

interface EditAjaxResponse {
    success: boolean;
    data?: { content?: string; message?: string };
}

interface EditCardElements {
    body: Element;
    displayEl: Element;
    rawEl: Element;
    footer: Element;
}

function getEditContainers(): Element[] {
    return Array.from(document.querySelectorAll('.updates-cards, .stats-v2-activity-cards'));
}

function getEditCardElements(card: Element): EditCardElements | null {
    const isActivityCard = card.classList.contains('activity-card');
    const body = isActivityCard ? card.querySelector('.activity-body') : card.querySelector('.card-body');
    if (!body) return null;
    const displayEl = isActivityCard ? body.querySelector('.activity-content') : body.querySelector('.card-content');
    const rawEl = isActivityCard ? card.querySelector('.card-content-raw') : body.querySelector('.card-content-raw');
    const footer = isActivityCard ? card.querySelector('.activity-footer') : body.querySelector('.card-footer');
    if (!displayEl || !rawEl || !footer) return null;
    return { body, displayEl, rawEl, footer };
}

function closeAllMenus(root: ParentNode, except: Element | null = null): void {
    root.querySelectorAll('.note-card-menu').forEach((menu) => {
        if (except && menu === except) return;
        const dropdown = menu.querySelector('.note-menu-dropdown');
        const btn = menu.querySelector('.note-menu-btn');
        if (dropdown instanceof HTMLElement) dropdown.hidden = true;
        if (btn) btn.setAttribute('aria-expanded', 'false');
    });
}

function toggleMenu(menuWrap: Element, root: ParentNode): void {
    const dropdown = menuWrap.querySelector('.note-menu-dropdown');
    const btn = menuWrap.querySelector('.note-menu-btn');
    if (!(dropdown instanceof HTMLElement) || !btn) return;
    const willOpen = dropdown.hidden;
    closeAllMenus(root, willOpen ? menuWrap : null);
    dropdown.hidden = !willOpen;
    btn.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
}

function getCardMenu(card: Element): Element | null {
    return card.querySelector('.note-card-menu');
}

function buildShareUrl(content: string, hasGoal: boolean): string {
    const suffix = hasGoal ? 'Goals ✓' : 'Goals';
    return 'https://wa.me/?text=' + encodeURIComponent(content + '\n_\n' + suffix);
}

function refreshShareHref(card: Element, raw: string): void {
    const share = card.querySelector('.update-menu-share');
    if (!(share instanceof HTMLAnchorElement)) return;
    const hasGoal = share.getAttribute('data-has-goal') === '1';
    share.href = buildShareUrl(raw, hasGoal);
}

function startEdit(card: Element, config: GoalsEditUpdateConfig): void {
    const postId = card.getAttribute('data-post-id');
    if (!postId) return;

    const elements = getEditCardElements(card);
    if (!elements) return;

    const { body, displayEl, rawEl, footer } = elements;
    const menuWrap = getCardMenu(card);

    if (body.querySelector('.update-edit-textarea')) return;

    closeAllMenus(document);
    if (menuWrap instanceof HTMLElement) menuWrap.hidden = true;
    if (card.classList.contains('activity-card')) {
        card.classList.add('activity-card--editing');
    }

    const rawContent = rawEl.textContent || '';

    const textarea = document.createElement('textarea');
    textarea.name = 'content';
    textarea.rows = 5;
    textarea.className = 'update-edit-textarea';
    textarea.value = rawContent;

    const btnWrap = document.createElement('div');
    btnWrap.className = 'update-edit-actions';
    const saveBtn = document.createElement('button');
    saveBtn.type = 'button';
    saveBtn.className = 'btn btn-sm btn-primary update-save-btn';
    saveBtn.textContent = 'Save';
    const cancelBtn = document.createElement('button');
    cancelBtn.type = 'button';
    cancelBtn.className = 'btn btn-sm update-cancel-btn';
    cancelBtn.textContent = 'Cancel';

    const messageEl = document.createElement('div');
    messageEl.className = 'update-edit-message';

    (displayEl as HTMLElement).style.display = 'none';

    const attach = (el: Node): void => {
        if (footer.parentElement === body) {
            body.insertBefore(el, footer);
        } else {
            body.appendChild(el);
        }
    };
    attach(textarea);
    btnWrap.appendChild(saveBtn);
    btnWrap.appendChild(cancelBtn);
    attach(btnWrap);
    attach(messageEl);
    textarea.focus();

    const cleanup = (): void => {
        textarea.remove();
        btnWrap.remove();
        messageEl.remove();
        (displayEl as HTMLElement).style.display = '';
        if (menuWrap instanceof HTMLElement) menuWrap.hidden = false;
        card.classList.remove('activity-card--editing');
    };

    const restore = (newDisplayHtml?: string, newRaw?: string): void => {
        if (newDisplayHtml) displayEl.innerHTML = newDisplayHtml;
        if (newRaw !== undefined) {
            rawEl.textContent = newRaw;
            refreshShareHref(card, newRaw);
        }
        cleanup();
    };

    cancelBtn.addEventListener('click', () => restore());

    saveBtn.addEventListener('click', async () => {
        const content = textarea.value.trim();
        if (!content) {
            messageEl.textContent = 'Please enter your update.';
            messageEl.className = 'update-edit-message error';
            return;
        }

        saveBtn.disabled = true;
        saveBtn.textContent = 'Saving…';
        messageEl.textContent = '';
        messageEl.className = 'update-edit-message';

        const formData = new FormData();
        formData.set('action', 'goals_edit_update');
        formData.set('_wpnonce', config.nonce);
        formData.set('post_id', postId);
        formData.set('content', content);

        try {
            const res = await fetch(config.ajaxUrl, { method: 'POST', body: formData });
            const data = (await res.json()) as EditAjaxResponse;

            if (data.success && data.data?.content) {
                restore(data.data.content, content);
            } else {
                const msg = data.data?.message || 'Something went wrong.';
                messageEl.textContent = msg;
                messageEl.className = 'update-edit-message error';
                saveBtn.disabled = false;
                saveBtn.textContent = 'Save';
            }
        } catch {
            messageEl.textContent = 'Something went wrong. Please try again.';
            messageEl.className = 'update-edit-message error';
            saveBtn.disabled = false;
            saveBtn.textContent = 'Save';
        }
    });
}

async function deleteUpdate(card: Element, config: GoalsEditUpdateConfig): Promise<void> {
    const postId = card.getAttribute('data-post-id');
    if (!postId) return;
    if (!config.deleteNonce) return;
    if (!window.confirm('Delete this update?')) return;

    closeAllMenus(document);

    const formData = new FormData();
    formData.set('action', 'goals_delete_update');
    formData.set('_wpnonce', config.deleteNonce);
    formData.set('post_id', postId);

    try {
        const res = await fetch(config.ajaxUrl, { method: 'POST', body: formData });
        const data = (await res.json()) as EditAjaxResponse;
        if (data.success) {
            card.remove();
            return;
        }
        window.alert(data.data?.message || 'Something went wrong.');
    } catch {
        window.alert('Something went wrong. Please try again.');
    }
}

function flashUpdateFromHash(): void {
    const match = /^#update-(\d+)$/.exec(window.location.hash);
    if (!match) {
        return;
    }
    const el = document.getElementById('update-' + match[1]);
    if (!(el instanceof HTMLElement)) {
        return;
    }
    el.scrollIntoView({ block: 'center' });
    el.classList.add('activity-card--flash');
    window.setTimeout(() => el.classList.remove('activity-card--flash'), 1600);
}

export function editUpdates(): void {
    flashUpdateFromHash();
    const containers = getEditContainers();
    if (!containers.length) return;

    const config = (window as Window & { goalsEditUpdate?: GoalsEditUpdateConfig }).goalsEditUpdate;
    if (!config?.ajaxUrl || !config?.nonce) return;

    containers.forEach((container) => {
        container.addEventListener('click', (e: Event) => {
            const target = e.target as HTMLElement;

            const menuBtn = target.closest('.note-menu-btn');
            if (menuBtn) {
                const menuWrap = menuBtn.closest('.note-card-menu');
                if (menuWrap && container.contains(menuWrap)) {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleMenu(menuWrap, container);
                }
                return;
            }

            const editBtn = target.closest('.update-menu-edit');
            if (editBtn) {
                e.preventDefault();
                const card = editBtn.closest('.update-card, .activity-card[data-post-id]');
                if (card) startEdit(card, config);
                return;
            }

            const deleteBtn = target.closest('.update-menu-delete');
            if (deleteBtn) {
                e.preventDefault();
                const card = deleteBtn.closest('.update-card, .activity-card[data-post-id]');
                if (card) void deleteUpdate(card, config);
            }
        });
    });

    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') closeAllMenus(document);
    });

    document.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        if (!target.closest('.note-card-menu')) closeAllMenus(document);
    });
}
